import {
  createFormSubmitHandler,
  NvForm,
  NvModal,
} from '@simwood/nv-react-components';
import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  createPhonebookEntry,
  editPhonebookEntry,
} from '../../reducers/contacts';
import { StoreDispatch, StoreState } from '../../store';
import { ApiPhonebookEntryCreate } from '../../types/PhonebookEntry';
import {
  denormalizePhonebookEntryFormValues,
  normalizePhonebookEntryFormDefaults,
  PhonebookEntryFormValues,
} from './phonebookEntryForm';

const priorityOptions = [
  { label: 'Low', value: '-50' },
  { label: 'Normal', value: '0' },
  { label: 'High', value: '50' },
];

interface Props {
  fullScreen?: boolean;
  contactId?: string;
  onClose: () => void;
}

const validPhoneNum = (phoneNumber: string) =>
  !!phoneNumber && phoneNumber.length >= 3 && phoneNumber.length <= 20;

const ContactEditModal: React.FC<Props> = ({
  // fullScreen,
  contactId,
  onClose,
}) => {
  const dispatch = useDispatch<StoreDispatch>();
  const phonebook = useSelector(
    (state: StoreState) => state.contacts.phonebook,
  );

  const contact = useMemo(() => {
    if (!contactId) {
      return undefined;
    }
    return phonebook.find((p) => p.id === contactId);
  }, [phonebook, contactId]);

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<PhonebookEntryFormValues>({
    mode: 'onBlur',
    defaultValues: normalizePhonebookEntryFormDefaults(contact),
  });

  const submit = useCallback(
    (modified: PhonebookEntryFormValues) => {
      const payload: ApiPhonebookEntryCreate =
        denormalizePhonebookEntryFormValues(modified);
      if (contactId) {
        dispatch(editPhonebookEntry({ id: contactId, entry: payload }));
      } else {
        dispatch(createPhonebookEntry(payload));
      }
    },
    [contactId, dispatch],
  );

  // FIXME used to support fullscreen
  return (
    <NvModal.Form
      title={contactId ? `Edit ${contact?.name}` : 'New phonebook contact'}
      open={true}
      closeText='Cancel'
      submitText={contactId ? 'Save' : 'Create'}
      disabled={!isDirty}
      closeOnSubmit
      onSubmit={createFormSubmitHandler(handleSubmit, submit)}
      setOpen={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <NvForm.Input
        labelText='Name'
        name='name'
        placeholder='Enter name here'
        control={control}
        // rules={{ required: 'Name is required' }}
        required
      />
      <NvForm.Input
        labelText='Phone number'
        name='phoneNumber'
        placeholder='Enter number here'
        rules={{
          validate: (p) =>
            validPhoneNum(p) ||
            'Phone number required (between 3 and 20 characters',
        }}
        control={control}
        required
      />
      <NvForm.Input
        labelText='Email'
        name='email'
        type='email'
        placeholder='Optional'
        control={control}
      />
      <NvForm.Input
        labelText='Speed dial'
        name='speedDial'
        type='number'
        placeholder='Optional'
        control={control}
      />
      <NvForm.Select
        name='priority'
        labelText='Priority'
        items={priorityOptions}
        control={control}
        asNumber
      />
    </NvModal.Form>
  );
};

export default ContactEditModal;
